import React from "react";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import {
  Box,
  Avatar,
  Stack,
  Alert,
  AlertTitle,
  Skeleton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import { useTheme, type Theme } from "@mui/material/styles";
import * as selectors from "selectors";
import { useSelector } from "react-redux";
import useGetCustomerPastClinicians from "hooks/api/client/useGetCustomerPastClinicians";

import { ArrowsLeftRight, IdentificationBadge, LinkBreak, Warning } from "@phosphor-icons/react";
import RequestNewClinician from "./RequestNewClinician";
import AssignClinician from "./AssignClinician";

const getPossessive = (name: string): string => {
  const lastChar = name.slice(-1);
  if (lastChar === "s") return `${name}'`;
  return `${name}'s`;
};

const getPrimaryClinicianRole = (clinician: any, secondaryClinician: any): string => {
  if (clinician?.is_director) {
    return "Clinical Director";
  }

  if (secondaryClinician?.is_supervisor) {
    return "Clinical Supervisor";
  }

  return "Primary Clinician";
};

const getSecondaryClinicianRole = (secondaryClinician: any): string => {
  if (secondaryClinician?.is_director) {
    return "Clinical Director";
  }

  if (secondaryClinician?.is_supervisor) {
    return "Clinical Supervisor";
  }

  return "Supervisory Clinician";
};

interface ClinicianSummary {
  is_director?: boolean;
  is_supervisor?: boolean;
  first_name?: string;
  last_name?: string;
  image_url?: string;
  manager_name?: string;
}

interface FamilyNavigatorDetails {
  family_navigator_user_id?: string | number | null;
  family_navigator_name?: string | null;
  family_navigator_first_name?: string | null;
  family_navigator_last_name?: string | null;
}

interface AssignedCliniciansCustomerDetails {
  clinician?: ClinicianSummary | null;
  secondaryClinician?: ClinicianSummary | null;
  details?: FamilyNavigatorDetails | null;
}

interface AssignedCliniciansPermissions {
  assign_clinician?: boolean;
  assign_secondary_clinician?: boolean;
  edit_client_info?: boolean;
  request_new_clinician?: boolean;
}

interface AssignedClinicianProps {
  editDisabled: boolean;
  familyNavigatorEditDisabled: boolean;
  assignClinicianOpen: boolean;
  onOpenAssignClinician: (role: string) => void;
  onCloseAssignClinician: () => void;
  onOpenAssignClinicianConfirm: (clinician: any) => void;
  onOpenRemoveClinicianConfirm: (clinician: any) => void;
  onOpenAssignFamilyNavigator: () => void;
  assignedRole?: string;
}

interface ClinicianRowSkeletonProps {
  hasSecondaryAction?: boolean;
}

const ClinicianRowSkeleton = ({ hasSecondaryAction = false }: ClinicianRowSkeletonProps) => {
  return (
    <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
      <Stack direction="row" gap={2} alignItems="center">
        <Skeleton variant="circular" width={40} height={40} />
        <Stack width="100%" minWidth={220}>
          <Skeleton variant="text" width={160} height={28} />
          <Skeleton variant="text" width={120} height={22} />
          <Skeleton variant="text" width={220} height={22} />
        </Stack>
      </Stack>
      <Stack direction="row" gap={3} alignItems="center">
        {hasSecondaryAction && <Skeleton variant="rounded" width={170} height={36} />}
        <Skeleton variant="rounded" width={210} height={36} />
      </Stack>
    </Stack>
  );
};

const ClinicianHistoryRowSkeleton = () => {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
      <Skeleton variant="text" width={140} height={24} />
      <Box sx={{ width: 16, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Skeleton variant="circular" width={8} height={8} />
      </Box>
      <Skeleton variant="text" width={130} height={24} />
    </Stack>
  );
};

const formatClinicianHistoryDate = (switchedAway?: string | null) => {
  if (!switchedAway) {
    return "Current";
  }

  return format(new Date(switchedAway), "MMMM do, yyyy");
};

const clinicianHistoryTitleId = "clinician-history-dialog-title";

const AssignedClinicians = ({
  editDisabled,
  familyNavigatorEditDisabled,
  assignClinicianOpen,
  onOpenAssignClinician,
  onCloseAssignClinician,
  onOpenAssignClinicianConfirm,
  onOpenRemoveClinicianConfirm,
  onOpenAssignFamilyNavigator,
  assignedRole,
}: AssignedClinicianProps) => {
  const theme = useTheme<Theme>();
  const [clinicianHistoryOpen, setClinicianHistoryOpen] = React.useState(false);
  const userPermissions = useSelector(
    selectors.getUserPermissionsList,
  ) as AssignedCliniciansPermissions | null;
  const customerDetails = useSelector(
    selectors.getCustomerDetails,
  ) as AssignedCliniciansCustomerDetails | null;
  const clientId = useSelector(selectors.getCustomerClientId) as string | number | undefined | null;
  const cliniciansLoading = useSelector(selectors.getCustomerCliniciansLoading);
  const { pastClinicians, pastCliniciansLoading, refetchPastClinicians } =
    useGetCustomerPastClinicians(clientId || undefined);
  const clinician = customerDetails?.clinician;
  const secondaryClinician = customerDetails?.secondaryClinician;
  const primaryClinicianRole = getPrimaryClinicianRole(clinician, secondaryClinician);
  const secondaryClinicianRole = getSecondaryClinicianRole(secondaryClinician);
  const isPrimaryClinicianLoading =
    Boolean(clinician) && !clinician?.first_name && !clinician?.last_name;
  const isSecondaryClinicianLoading =
    Boolean(secondaryClinician) &&
    !secondaryClinician?.first_name &&
    !secondaryClinician?.last_name;
  const showCareTeamSkeleton =
    cliniciansLoading || isPrimaryClinicianLoading || isSecondaryClinicianLoading;
  const hasFamilyNavigator = Boolean(customerDetails?.details?.family_navigator_user_id);
  const showBorderedCareTeam =
    showCareTeamSkeleton || Boolean(clinician) || Boolean(secondaryClinician) || hasFamilyNavigator;

  const initials = `${clinician?.first_name?.[0]?.toUpperCase() || ""}${clinician?.last_name?.[0]?.toUpperCase() || ""}`;
  const secondaryInitials = `${secondaryClinician?.first_name?.[0]?.toUpperCase() || ""}${secondaryClinician?.last_name?.[0]?.toUpperCase() || ""}`;

  const sortedPastClinicians = React.useMemo(() => {
    return [...pastClinicians].sort((a, b) => {
      const aSwitchedAwayTime = a?.switched_away ? new Date(a.switched_away).getTime() : null;
      const bSwitchedAwayTime = b?.switched_away ? new Date(b.switched_away).getTime() : null;

      if (aSwitchedAwayTime === null && bSwitchedAwayTime === null) {
        return 0;
      }

      if (aSwitchedAwayTime === null) {
        return -1;
      }

      if (bSwitchedAwayTime === null) {
        return 1;
      }

      return bSwitchedAwayTime - aSwitchedAwayTime;
    });
  }, [pastClinicians]);

  React.useEffect(() => {
    if (!clinicianHistoryOpen || !clientId) {
      return;
    }

    refetchPastClinicians();
  }, [clinicianHistoryOpen, clientId, refetchPastClinicians]);

  let clinicianHistoryContent: React.ReactNode;

  if (pastCliniciansLoading) {
    clinicianHistoryContent = (
      <Stack spacing={3}>
        <ClinicianHistoryRowSkeleton />
        <ClinicianHistoryRowSkeleton />
        <ClinicianHistoryRowSkeleton />
      </Stack>
    );
  } else if (sortedPastClinicians.length > 0) {
    clinicianHistoryContent = (
      <Timeline>
        {sortedPastClinicians.map((pastClinician, index) => {
          const isLast = index === sortedPastClinicians.length - 1;

          return (
            <TimelineItem
              key={
                pastClinician.id ||
                `${pastClinician.first_name}-${pastClinician.last_name}-${index}`
              }
            >
              <TimelineOppositeContent>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ width: "100%", minWidth: 0, textAlign: "right" }}
                >
                  {formatClinicianHistoryDate(pastClinician.switched_away)}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot variant="outlined" />
                {!isLast && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ width: "100%", minWidth: 0 }}
                >
                  {`${pastClinician.first_name} ${pastClinician.last_name}`}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    );
  } else {
    clinicianHistoryContent = (
      <Typography variant="body2" color="text.secondary">
        No clinician history found for this client.
      </Typography>
    );
  }

  const renderPrimaryClinicianRow = () => {
    if (showCareTeamSkeleton) {
      return <ClinicianRowSkeleton />;
    }

    if (!clinician) {
      return (
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"100%"}
        >
          <Box display={"flex"} gap={"8px"}>
            <Warning weight="duotone" fill={theme.palette.warning.main} size={24} />
            <Box justifyContent={"center"} alignItems={"center"} display={"flex"}>
              <Typography variant="body1" color={"text.secondary"}>
                No Clinician assigned
              </Typography>
            </Box>
          </Box>
          {userPermissions?.assign_clinician && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onOpenAssignClinician("primary")}
              disabled={editDisabled}
              startIcon={<IdentificationBadge weight="duotone" />}
            >
              Assign Clinician
            </Button>
          )}
        </Stack>
      );
    }

    return (
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Stack direction="row" gap={2} alignItems="center">
          <Avatar src={clinician.image_url} sx={{ width: 40, height: 40 }}>
            {initials}
          </Avatar>
          <Stack>
            <Typography variant="body1">{`${clinician.first_name} ${clinician.last_name}`}</Typography>
            <Typography variant="body2" color="text.secondary">
              {primaryClinicianRole}
            </Typography>
            {clinician?.manager_name && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                {`${getPossessive(
                  `${clinician.first_name} ${clinician.last_name}`
                )} manager is ${clinician?.manager_name}`}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" gap={3} alignItems="center">
          {userPermissions?.assign_clinician && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onOpenAssignClinician("primary")}
              disabled={editDisabled}
              startIcon={<ArrowsLeftRight weight="duotone" />}
            >
              Reassign Primary Clinician
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderSecondaryClinicianRow = () => {
    if (showCareTeamSkeleton) {
      return <ClinicianRowSkeleton hasSecondaryAction />;
    }

    if (!secondaryClinician) {
      return (
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"100%"}
        >
          <Box display={"flex"} gap={"8px"} justifyContent={"center"} alignItems={"center"}>
            <Warning weight="duotone" fill="#ED6C02" size={24} />
            <Box justifyContent={"center"} alignItems={"center"} display={"flex"} height={"100%"}>
              <Typography variant="body1" color={"text.secondary"}>
                No Supervisor assigned
              </Typography>
            </Box>
          </Box>
          {userPermissions?.assign_secondary_clinician && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onOpenAssignClinician("secondary")}
              disabled={editDisabled || !clinician}
              startIcon={<IdentificationBadge weight="duotone" />}
            >
              Assign Supervisor
            </Button>
          )}
        </Stack>
      );
    }

    return (
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Stack direction="row" gap={2} alignItems="center">
          <Avatar src={secondaryClinician.image_url}>{secondaryInitials}</Avatar>
          <Stack>
            <Typography variant="body1">{`${secondaryClinician.first_name} ${secondaryClinician.last_name}`}</Typography>
            <Typography variant="body2" color="text.secondary">
              {secondaryClinicianRole}
            </Typography>
            {secondaryClinician?.manager_name && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                {`${getPossessive(
                  `${secondaryClinician.first_name} ${secondaryClinician.last_name}`
                )} manager is ${secondaryClinician?.manager_name}`}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" gap={3} alignItems="center">
          {userPermissions?.assign_secondary_clinician && (
            <Button
              aria-label="Remove as Supervisor"
              variant="contained"
              color="secondary"
              disabled={editDisabled}
              onClick={() => onOpenRemoveClinicianConfirm(secondaryClinician)}
              startIcon={<LinkBreak />}
            >
              Remove as Supervisor
            </Button>
          )}
          {userPermissions?.assign_secondary_clinician && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onOpenAssignClinician("secondary")}
              disabled={editDisabled}
              startIcon={<ArrowsLeftRight weight="duotone" />}
            >
              Reassign Supervisor
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderFamilyNavigatorRow = () => {
    const familyNavigatorDetails = customerDetails?.details;
    const hasFamilyNavigator = familyNavigatorDetails?.family_navigator_user_id != null;
    const familyNavigatorName = familyNavigatorDetails?.family_navigator_name || "";
    const familyNavigatorFirstName = familyNavigatorDetails?.family_navigator_first_name?.trim();
    const familyNavigatorLastName = familyNavigatorDetails?.family_navigator_last_name?.trim();
    const familyNavigatorNameInitials = familyNavigatorName
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((namePart) => namePart[0])
      .join("")
      .toUpperCase();
    const familyNavigatorInitials =
      familyNavigatorFirstName && familyNavigatorLastName
        ? `${familyNavigatorFirstName[0]}${familyNavigatorLastName[0]}`.toUpperCase()
        : familyNavigatorNameInitials;

    if (!hasFamilyNavigator) {
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Box display="flex" gap="8px" alignItems="center">
            <Warning weight="duotone" fill={theme.palette.warning.main} size={24} />
            <Typography variant="body1" color="text.secondary">
              No Family Navigator assigned
            </Typography>
          </Box>
          {userPermissions?.edit_client_info && (
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              disabled={familyNavigatorEditDisabled}
              onClick={onOpenAssignFamilyNavigator}
              startIcon={<IdentificationBadge weight="duotone" />}
            >
              Assign Family Navigator
            </Button>
          )}
        </Stack>
      );
    }

    return (
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Stack direction="row" gap={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40 }}>{familyNavigatorInitials}</Avatar>
          <Stack>
            <Typography variant="body1">{familyNavigatorName}</Typography>
            <Typography variant="body2" color="text.secondary">
              Family Navigator
            </Typography>
          </Stack>
        </Stack>
        {userPermissions?.edit_client_info && !clinician && (
          <Button
            variant="contained"
            color="secondary"
            size="medium"
            disabled={familyNavigatorEditDisabled}
            onClick={onOpenAssignFamilyNavigator}
            startIcon={<ArrowsLeftRight weight="duotone" />}
          >
            Reassign Family Navigator
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Stack gap={7}>
      <Stack>
        <Typography variant="h3">AnswersNow Care Team</Typography>
        <Typography variant="body2" color="text.secondary">
          Set the primary and supervisory clinicians for the client
        </Typography>
      </Stack>
      <Stack gap={5}>
        <Stack
          borderRadius={showBorderedCareTeam ? 4 : 0}
          border={showBorderedCareTeam ? `1px solid ${theme.palette.divider}` : "none"}
          overflow={showBorderedCareTeam ? "hidden" : "visible"}
        >
          <Stack px={5} py={5}>
            {renderPrimaryClinicianRow()}
          </Stack>
          <Stack px={5} py={5}>
            {renderSecondaryClinicianRow()}
          </Stack>
          <Stack px={5} py={5}>
            {renderFamilyNavigatorRow()}
          </Stack>
          <Box sx={{ p: 5 }}>
            {showCareTeamSkeleton ? (
              <Stack gap={1}>
                <Skeleton variant="text" width={180} height={28} />
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="85%" height={20} />
              </Stack>
            ) : (
              <Alert severity="info">
                <AlertTitle>What&rsquo;s a Supervisor?</AlertTitle>
                Supervisors are required for non-credentialed BCBA&rsquo;s only. For coverage,
                shadow sessions, or if a supervisor joins a call with a credentialed clinician,
                simply add them to the call directly.
              </Alert>
            )}
          </Box>
        </Stack>

        {showCareTeamSkeleton ? (
          <Skeleton variant="text" width={140} height={24} sx={{ alignSelf: "flex-start" }} />
        ) : (
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="none"
            onClick={(event) => {
              event.currentTarget.blur();
              setClinicianHistoryOpen(true);
            }}
            sx={{ alignSelf: "flex-start" }}
          >
            View Clinician History
          </Link>
        )}

        {userPermissions?.request_new_clinician &&
          (showCareTeamSkeleton ? (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack width="100%" maxWidth={452}>
                <Skeleton variant="text" width={170} height={28} />
                <Skeleton variant="text" width="100%" height={22} />
              </Stack>
              <Skeleton variant="rounded" width={40} height={24} />
            </Stack>
          ) : (
            <RequestNewClinician />
          ))}
      </Stack>

      <Dialog
        open={clinicianHistoryOpen}
        onClose={() => setClinicianHistoryOpen(false)}
        aria-labelledby={clinicianHistoryTitleId}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id={clinicianHistoryTitleId} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" color="text.primary">
            Clinician History
          </Typography>
          <IconButton
            aria-label="Close clinician history dialog"
            onClick={() => setClinicianHistoryOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>{clinicianHistoryContent}</DialogContent>
      </Dialog>

      <AssignClinician
        open={assignClinicianOpen}
        assignedRole={assignedRole}
        onCloseAssignClinician={onCloseAssignClinician}
        onOpenAssignClinicianConfirm={onOpenAssignClinicianConfirm}
        clinician={clinician}
        secondaryClinician={secondaryClinician}
      />

    </Stack>
  );
};

export default AssignedClinicians;
